// src/pages/admin/AdminAnnouncement.js — "ลงประกาศ": the admin uploads a newspaper / announcement PDF for an issue date.
// The file is uploaded to the server (POST /api/announcement/upload). It used to be kept only as a browser blob: URL,
// so it was gone after a reload and nobody else could open it.
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAnnouncements } from "../../context/AnnouncementContext";
import { deleteAnnouncement, fileHref, uploadAnnouncementImage } from "../../services/apiClient";
import "./AdminAnnouncement.css";
import DateInput from "../../components/site/DateInput";
import { formatDate } from "../../utils/dateFormat";
import { PageNav, PageSummary, usePaging } from "../../components/admin/Pager";

const MAX_MB = 25; // same limit as the server

function AdminAnnouncement() {
  const { announcements, reloadAnnouncements } = useAnnouncements();
  const paging = usePaging(announcements, "mng_announce_page_size", "");
  const navigate = useNavigate();
  const fileInput = useRef(null);

  const [title, setTitle] = useState("");
  // default: today in Bangkok — most newspapers are uploaded on their own issue day
  const todayBangkok = () => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);
  const [date, setDate] = useState(todayBangkok);
  const [pdfFile, setPdfFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  // HashRouter: window.location.href = "/admin/..." left the app and landed on the home page
  const handleEdit = (id) => navigate(`/admin/announcements/${id}/edit`);

  const handleDelete = async (a) => {
    if (!window.confirm(`ต้องการลบ "${a.title || "ประกาศนี้"}" ใช่ไหม?`)) return;
    setMsg(null);
    try {
      await deleteAnnouncement(a.id);
      await reloadAnnouncements();
      setMsg({ ok: true, text: "ลบใบประกาศเรียบร้อยแล้ว" });
    } catch (err) {
      setMsg({ ok: false, text: `ลบไม่สำเร็จ: ${err.message}` });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!date) return setMsg({ ok: false, text: "กรุณาใส่วันที่หนังสือพิมพ์ออก" });
    if (!pdfFile) return setMsg({ ok: false, text: "กรุณาเลือกไฟล์ PDF" });
    if (!/\.pdf$/i.test(pdfFile.name)) return setMsg({ ok: false, text: "รองรับเฉพาะไฟล์ PDF" });
    if (pdfFile.size > MAX_MB * 1024 * 1024) return setMsg({ ok: false, text: `ไฟล์ใหญ่เกิน ${MAX_MB} MB` });

    setBusy(true);
    try {
      await uploadAnnouncementImage({
        fileOrBlob: pdfFile,
        filename: pdfFile.name,
        issueDate: date,
        title: title.trim() || `ประกาศลงวันที่ ${formatDate(date)}`,
      });
      await reloadAnnouncements();
      setTitle("");
      setDate(todayBangkok());
      setPdfFile(null);
      if (fileInput.current) fileInput.current.value = "";
      setMsg({ ok: true, text: "บันทึกประกาศเรียบร้อยแล้ว · อัปโหลดไฟล์ขึ้นเซิร์ฟเวอร์แล้ว" });
    } catch (err) {
      setMsg({ ok: false, text: `บันทึกไม่สำเร็จ: ${err.message}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-page">
      <h2>ลงประกาศหนังสือพิมพ์ (PDF)</h2>

      <form className="admin-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>หัวข้อ (ไม่บังคับ)</label>
          <input
            className="form-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="เช่น หนังสือพิมพ์ประจำวันที่ ..."
          />
        </div>

        <div className="form-group">
          <label>
            วันที่หนังสือพิมพ์ออก <span className="required">**</span>
          </label>
          <DateInput
            className="form-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>
            อัปโหลดไฟล์ PDF <span className="required">**</span>
          </label>
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf,.pdf"
            className="form-input"
            onChange={(e) => setPdfFile(e.target.files[0] || null)}
          />
          {pdfFile && <small>ไฟล์ที่เลือก: {pdfFile.name}</small>}
        </div>

        {msg && <p className={`adm-msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</p>}

        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? "กำลังอัปโหลด..." : "บันทึกประกาศ"}
        </button>
      </form>

      <hr />

      <h3>รายการประกาศล่าสุด</h3>
      {announcements.length > 0 && <PageSummary paging={paging} />}
      <ul className="admin-list">
        {announcements.length === 0 && <li>ยังไม่มีประกาศ</li>}

        {paging.shown.map((a) => (
          <li key={a.id} className="admin-item">
            <strong>{formatDate(a.issueDate || a.date) || "-"}</strong> — {a.title}{" "}
            {(a.fileUrl || a.imageUrl) && (
              <a href={fileHref(a.fileUrl || a.imageUrl)} target="_blank" rel="noreferrer">
                เปิดไฟล์
              </a>
            )}{" "}
            <button type="button" className="btn-edit" onClick={() => handleEdit(a.id)}>
              แก้ไข
            </button>{" "}
            <button type="button" className="btn-delete" onClick={() => handleDelete(a)}>
              ลบ
            </button>
          </li>
        ))}
      </ul>
      <PageNav paging={paging} />
    </div>
  );
}

export default AdminAnnouncement;
